import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

import { getTemplateData } from "../../data/templateData";
import { generateId } from "../../utils/helpers";

const ResumeContext = createContext(undefined);
const LOCAL_STORAGE_KEY = "resumeData";

export const useResumeContext = () => {
  const context = useContext(ResumeContext);
  if (!context) {
    throw new Error("useResumeContext must be used within a ResumeProvider");
  }
  return context;
};

export const ResumeProvider = ({ children, templateType }) => {
  const getInitialData = () => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // fallback to template if corrupted
      }
    }
    // Use your dummy/template data instead of blank fields
    return getTemplateData(templateType); // <-- Use your template data here
  };

  const [resumeData, setResumeData] = useState(getInitialData());
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(resumeData));
    setLastSaved(new Date());
  }, [resumeData]);

  const updateResumeData = (data) => {
    setResumeData((prev) => ({ ...prev, ...data }));
  };

  const updatePersonalInfo = (info) => {
    setResumeData((prev) => ({
      ...prev,
      personalInfo: { ...prev.personalInfo, ...info },
    }));
  };

  const updateSummary = (summary) => {
    setResumeData((prev) => ({ ...prev, summary }));
  };

  const updateSkills = (skills) => {
    setResumeData((prev) => ({ ...prev, skills }));
  };

  const addExperience = () => {
    const newExperience = {
      id: generateId(),
      company: "New Company",
      position: "Position Title",
      location: "Location",
      startDate: "",
      endDate: "",
      description: "Describe your responsibilities and achievements",
      achievements: ["Achievement 1", "Achievement 2"],
    };

    setResumeData((prev) => ({
      ...prev,
      experience: [newExperience, ...prev.experience],
    }));
  };

  const updateExperience = (id, experience) => {
    setResumeData((prev) => {
      // Find the experience to update
      const existingExp = prev.experience.find((exp) => exp.id === id);
      if (!existingExp) return prev;

      // Create the updated experience object
      const updatedExp = {
        ...existingExp,
        ...experience,
      };

      // Create new experience array with the updated item
      const updatedExperience = prev.experience.map((exp) =>
        exp.id === id ? updatedExp : exp
      );

      // Return new state
      return {
        ...prev,
        experience: updatedExperience,
      };
    });
  };

  const removeExperience = (id) => {
    setResumeData((prev) => ({
      ...prev,
      experience: prev.experience.filter((exp) => exp.id !== id),
    }));
  };

  const addProject = () => {
    const newProject = {
      id: generateId(),
      name: "New Project",
      description: "Describe your project",
      technologies: ["Technology 1", "Technology 2"],
    };

    setResumeData((prev) => ({
      ...prev,
      projects: [...prev.projects, newProject],
    }));
  };

  const updateProject = (id, project) => {
    setResumeData((prev) => ({
      ...prev,
      projects: prev.projects.map((proj) =>
        proj.id === id ? { ...proj, ...project } : proj
      ),
    }));
  };

  const removeProject = (id) => {
    setResumeData((prev) => ({
      ...prev,
      projects: prev.projects.filter((proj) => proj.id !== id),
    }));
  };

  const addEducation = () => {
    const newEducation = {
      id: generateId(),
      school: "School/University Name",
      degree: "Degree",
      fieldOfStudy: "Field of Study",
      startDate: "",
      endDate: "",
      description: "Describe your education",
    };

    setResumeData((prev) => ({
      ...prev,
      education: [...prev.education, newEducation],
    }));
  };

  const updateEducation = (id, education) => {
    setResumeData((prev) => {
      const updatedEducation = prev.education.map((edu) =>
        edu.id === id ? { ...edu, ...education } : edu
      );
      console.log("Updating education:", { id, education, updatedEducation }); // Debug log
      return {
        ...prev,
        education: updatedEducation,
      };
    });
  };

  const removeEducation = (id) => {
    setResumeData((prev) => ({
      ...prev,
      education: prev.education.filter((edu) => edu.id !== id),
    }));
  };

  const addCertification = () => {
    const newCertification = {
      id: generateId(),
      name: "Certification Name",
      issuer: "Issuing Organization",
      date: "",
      link: "",
    };

    setResumeData((prev) => ({
      ...prev,
      certifications: [...prev.certifications, newCertification],
    }));
  };

  const updateCertification = (id, certification) => {
    setResumeData((prev) => ({
      ...prev,
      certifications: prev.certifications.map((cert) =>
        cert.id === id ? { ...cert, ...certification } : cert
      ),
    }));
  };

  const removeCertification = (id) => {
    setResumeData((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((cert) => cert.id !== id),
    }));
  };

  const resetToTemplate = () => {
    const template = getInitialData();
    setResumeData(template);
    // Optionally clear localStorage:
    // localStorage.removeItem(LOCAL_STORAGE_KEY);
  };

  const value = {
    resumeData,
    templateType,
    updateResumeData,
    updatePersonalInfo,
    updateSummary,
    updateSkills,
    addExperience,
    updateExperience,
    removeExperience,
    addProject,
    updateProject,
    removeProject,
    addEducation,
    updateEducation,
    removeEducation,
    addCertification,
    updateCertification,
    removeCertification,
    resetToTemplate,
  };

  return (
    <ResumeContext.Provider value={value}>{children}</ResumeContext.Provider>
  );
};
